// apps/client/src/ui-kit/ExitGameModal.tsx
//
// Exit confirmation — white card, English copy, friendlier tone.
// "Keep Playing" (yellow) / "Exit to Menu" (grey)
//

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import { imgExitGame } from '../assets';

interface ExitGameModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
}

export const ExitGameModal: React.FC<ExitGameModalProps> = ({ isOpen, onClose, onConfirm }) => {
    const { t } = useTranslation();

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                    />

                    {/* Modal — white card */}
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0, y: 20 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.9, opacity: 0, y: 20 }}
                        className="relative w-full max-w-sm bg-white rounded-3xl p-6 shadow-card-white"
                    >
                        {/* Close X */}
                        <button
                            onClick={onClose}
                            className="absolute top-4 right-4 text-[#1A0B2E]/30 hover:text-[#1A0B2E]/60"
                        >
                            <Icon name="close" />
                        </button>

                        <div className="text-center">
                            <img src={imgExitGame} alt="" className="w-20 h-20 mx-auto mb-4" />
                            <h2 className="text-xl font-black text-[#1A0B2E] mb-2 font-display">
                                {t('game.exitTitle')}
                            </h2>
                            <p className="text-[#1A0B2E]/50 text-sm mb-6">
                                {t('game.exitDesc')}
                            </p>

                            <div className="flex flex-col gap-3">
                                {/* Primary: Stay (Thumb Zone — easier to tap) */}
                                <motion.button
                                    whileTap={{ scale: 0.95 }}
                                    onClick={onClose}
                                    className="w-full py-3.5 rounded-pill bg-gold-gradient text-[#1a0b2e] font-black text-base uppercase tracking-wider shadow-neon-gold"
                                >
                                    {t('game.exitKeep')}
                                </motion.button>

                                {/* Secondary: Exit */}
                                <motion.button
                                    whileTap={{ scale: 0.95 }}
                                    onClick={onConfirm}
                                    className="w-full py-3 rounded-pill bg-gray-100 text-[#1A0B2E]/50 font-bold text-sm uppercase tracking-wider hover:bg-gray-200 transition-all"
                                >
                                    {t('game.exitMenu')}
                                </motion.button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};
